import { z } from 'zod';
/**
 * A browser tool's output, or the error it answers with when it fails. TanStack checks a browser tool's answer against
 * its output schema, and only resumes a run if the browser's schema and the server's hash the same; an error the
 * schema doesn't allow leaves the run waiting and the chat refusing new messages. The server and the browser both
 * wrap each browser tool's schema with this.
 */
export const withToolError = (outputSchema) => z.union([outputSchema, z.object({ error: z.string() })]);
export const listAgentsToolSchema = {
    name: 'list_agents',
    description: "List the organization's AI agents with their ids, names and objectives, and whether the current user can edit each one. Answer questions about all or many agents from this list: reading every draft is slow, so read one (get_agent_draft) only for an agent the user asks about, and offer to for the rest. When an objective is null, say it has none set, and never infer what the agent does from its name.",
    inputSchema: z.object({}),
    outputSchema: z.object({
        agents: z.array(z.object({
            id: z.string(),
            name: z.string(),
            objective: z.string().nullable().describe('What the agent is for; null when none is set'),
            canEdit: z.boolean().describe('Whether the current user can edit this agent')
        }))
    })
};
export const getCurrentPageToolSchema = {
    name: 'get_current_page',
    description: 'Describe what the user is looking at right now: the route, URL and page title, the elements you can highlight on it, and any state the page reports (such as unsaved changes). Call this before highlighting, and after navigating.',
    inputSchema: z.object({}),
    outputSchema: z.object({
        routeId: z.string(),
        url: z.string(),
        title: z.string().optional(),
        anchors: z
            .array(z.object({ id: z.string(), label: z.string(), description: z.string().optional() }))
            .describe('Elements on this page that can be highlighted, by anchor id'),
        pageState: z.array(z.string()).describe('Facts the page reports about its current state')
    })
};
export const navigateToolSchema = {
    name: 'navigate',
    description: 'Take the user to a page in the app. `to` is a route path such as "/$orgSlug/app/agents/$agentId/conversation". Provide every $param except $orgSlug, which defaults to the current organization. Pass search values in their own shape, not as JSON strings: History\'s dateRange is an object, {"from": ISO date, "to": ISO date}.',
    inputSchema: z.object({
        to: z.string(),
        params: z.record(z.string(), z.string()).optional(),
        search: z
            .record(z.string(), z.union([
            z.string(),
            z.number(),
            z.boolean(),
            z.array(z.string()),
            z.record(z.string(), z.union([z.string(), z.array(z.string())]))
        ]))
            .optional()
    }),
    outputSchema: z.object({
        url: z.string(),
        previousUrl: z.string()
    })
};
export const highlightToolSchema = {
    name: 'highlight',
    description: 'Spotlight an element on the current page so the user can find it. Use an anchor id from get_current_page, and a short message saying what it is or what to do there.',
    inputSchema: z.object({
        anchorId: z.string(),
        message: z.string().optional()
    }),
    outputSchema: z.object({
        highlighted: z.boolean()
    })
};
/**
 * What the user can send back when they reject a change Via proposed. Shared so the server and the
 * browser agree on the approval binding.
 */
export const viaApprovalSchema = {
    reject: z.object({ reason: z.string().optional().describe('Why the user rejected the change') })
};
export const searchDocsToolSchema = {
    name: 'search_docs',
    description: "Search Strada's product documentation. Use it before answering any question about how a feature works or how to configure it. Returns the best-matching pages with a snippet; read the most relevant one with read_doc before answering.",
    inputSchema: z.object({
        query: z
            .string()
            .describe('Keywords for the feature or task, such as "alert conditions" or "publish agent version"')
    }),
    outputSchema: z.object({
        results: z.array(z.object({
            path: z.string(),
            title: z.string(),
            description: z.string().optional(),
            url: z.string(),
            snippet: z.string()
        }))
    })
};
export const readDocToolSchema = {
    name: 'read_doc',
    description: 'Read a documentation page by its path from search_docs. When you answer from it, link the page url so the user can read more.',
    inputSchema: z.object({
        path: z.string().describe('Page path from search_docs, such as "deployment/alerts"')
    }),
    outputSchema: z.object({
        title: z.string(),
        url: z.string(),
        content: z.string()
    })
};
//# sourceMappingURL=tool-schemas.js.map